import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, setAuthToken } from '@/lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState({ hack: new Set(), product: new Set() });
  const [credits, setCredits] = useState(0);
  const [promo, setPromo] = useState({ active: false, until: null });

  // Fetch promo / config state on mount (no auth required)
  useEffect(() => {
    api.get('/credits/config')
      .then(({ data }) => {
        const p = data?.free_promo || {};
        setPromo({
          active: !!p.active,
          until: p.until || null,
        });
      })
      .catch(() => {});
  }, []);

  const refreshCredits = useCallback(async () => {
    try {
      const { data } = await api.get('/credits/balance');
      const n = Number(data?.credits) || 0;
      setCredits(n);
      setUser((prev) => (prev ? { ...prev, credits: n } : prev));
      return n;
    } catch (e) {
      return null;
    }
  }, []);

  const setCreditsLocal = useCallback((value) => {
    const n = Number(value) || 0;
    setCredits(n);
    setUser((prev) => (prev ? { ...prev, credits: n } : prev));
  }, []);

  const refreshFavorites = useCallback(async () => {
    try {
      const { data } = await api.get('/favorites');
      const next = { hack: new Set(), product: new Set() };
      data.forEach((f) => next[f.item_type]?.add(f.item_id));
      setFavorites(next);
    } catch (e) {
      // ignore
    }
  }, []);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem('radiance_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      setCredits(Number(data?.credits) || 0);
      await refreshFavorites();
    } catch (e) {
      setAuthToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [refreshFavorites]);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setAuthToken(data.token);
    setUser(data.user);
    setCredits(Number(data.user?.credits) || 0);
    await refreshFavorites();
    return data.user;
  };

  const signup = async (name, email, password) => {
    const { data } = await api.post('/auth/signup', { name, email, password });
    setAuthToken(data.token);
    setUser(data.user);
    setCredits(Number(data.user?.credits) || 0);
    setFavorites({ hack: new Set(), product: new Set() });
    return data.user;
  };

  const logout = () => {
    setAuthToken(null);
    setUser(null);
    setCredits(0);
    setFavorites({ hack: new Set(), product: new Set() });
  };

  const updateProfile = async (payload) => {
    const { data } = await api.put('/auth/me', payload);
    setUser(data);
    setCredits(Number(data?.credits) || 0);
    return data;
  };

  const isFavorite = (type, id) => favorites[type]?.has(id);

  const toggleFavorite = async (type, id) => {
    if (!user) return { needsLogin: true };
    const already = favorites[type].has(id);
    // optimistic
    setFavorites((prev) => {
      const next = { hack: new Set(prev.hack), product: new Set(prev.product) };
      if (already) next[type].delete(id);
      else next[type].add(id);
      return next;
    });
    try {
      if (already) {
        await api.delete('/favorites', { params: { item_type: type, item_id: id } });
      } else {
        await api.post('/favorites', { item_type: type, item_id: id });
      }
      return { ok: true, added: !already };
    } catch (e) {
      // rollback
      setFavorites((prev) => {
        const next = { hack: new Set(prev.hack), product: new Set(prev.product) };
        if (already) next[type].add(id);
        else next[type].delete(id);
        return next;
      });
      return { error: true };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        signup,
        logout,
        updateProfile,
        favorites,
        isFavorite,
        toggleFavorite,
        refreshFavorites,
        credits,
        refreshCredits,
        setCreditsLocal,
        promo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
